import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Pagination } from '../../../components/ui/Pagination'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import { useArmas, useCreateArma, useUpdateArma, useDeleteArma } from '../hooks/useArmas'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import { armaFormSchema, type ArmaFormData } from '../schemas'
import type { Arma } from '../types'

const TIPOS_ARMA = ['Pistola', 'Revolver', 'Rifle', 'Carabina', 'Espingarda', 'Outro']

export function ArmasPage() {
  const [searchParams] = useSearchParams()
  const preloadAtiradorId = searchParams.get('idatirador') ? parseInt(searchParams.get('idatirador')!, 10) : undefined

  const [page, setPage] = useState(1)
  const [filterAtirador, setFilterAtirador] = useState(preloadAtiradorId)
  const [filterAtiradorSearch, setFilterAtiradorSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Arma | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Arma | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data, isLoading } = useArmas({ idatirador: filterAtirador, page, limit: 20 })
  const { data: atiradorData } = useAtiradores({ nome: filterAtiradorSearch, limit: 50 })
  const createArma = useCreateArma()
  const updateArma = useUpdateArma()
  const deleteArma = useDeleteArma()

  const form = useForm<ArmaFormData>({
    resolver: zodResolver(armaFormSchema),
    defaultValues: { idatirador: preloadAtiradorId || 0, marca: '', modelo: '', calibre: '', tipo: '' },
  })

  useEffect(() => {
    if (preloadAtiradorId) {
      form.setValue('idatirador', preloadAtiradorId)
    }
  }, [preloadAtiradorId, form])

  function openCreate() {
    setEditing(null)
    form.reset({ idatirador: filterAtirador || 0, marca: '', modelo: '', calibre: '', tipo: '' })
    setModalOpen(true)
  }

  function openEdit(a: Arma) {
    setEditing(a)
    form.reset({ idatirador: Number(a.idatirador), marca: a.marca, modelo: a.modelo, calibre: a.calibre, tipo: a.tipo })
    setModalOpen(true)
  }

  async function handleSubmit(formData: ArmaFormData) {
    try {
      if (editing) {
        await updateArma.mutateAsync({ id: editing.idarma, data: formData })
        setAlert({ type: 'success', message: 'Arma atualizada com sucesso' })
      } else {
        await createArma.mutateAsync(formData)
        setAlert({ type: 'success', message: 'Arma cadastrada com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar arma' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteArma.mutateAsync(deleteTarget.idarma)
      setAlert({ type: 'success', message: 'Arma excluida com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir arma' })
    } finally {
      setDeleteTarget(null)
    }
  }

  const meta = data?.meta
  const isSaving = createArma.isPending || updateArma.isPending

  const atiradorLabel = filterAtirador && data?.data?.[0]?.atirador_nome
    ? `Armas de ${data.data[0].atirador_nome}`
    : 'Armas'

  return (
    <div className="space-y-4">
      <PageHeader
        title={atiradorLabel}
        description="Gerenciar armas dos atiradores"
        action={
          <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
            + Nova Arma
          </button>
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Filtro por atirador */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar Atirador</label>
            <input className={inputClass} placeholder="Digite o nome do atirador..."
              value={filterAtiradorSearch}
              onChange={e => setFilterAtiradorSearch(e.target.value)} />
          </div>
          {filterAtiradorSearch && atiradorData?.data?.length ? (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Selecionar</label>
              <select className={selectClass} value={filterAtirador || ''}
                onChange={e => { setFilterAtirador(parseInt(e.target.value, 10) || undefined); setFilterAtiradorSearch(''); setPage(1) }}>
                <option value="">Todos os atiradores</option>
                {atiradorData.data.map(a => (
                  <option key={a.idatirador} value={a.idatirador}>{a.nome} — {a.cpf}</option>
                ))}
              </select>
            </div>
          ) : null}
          {filterAtirador && (
            <button onClick={() => { setFilterAtirador(undefined); setPage(1) }}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">
              Ver todas
            </button>
          )}
        </div>
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🔫</p>
            <p className="font-medium">Nenhuma arma encontrada</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Atirador</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Marca</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Modelo</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Calibre</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Tipo</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {data.data.map((a) => (
                  <tr key={a.idarma} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 text-compumaq-textoSuave">{a.atirador_nome}</td>
                    <td className="px-4 py-3 font-medium text-compumaq-texto">{a.marca}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{a.modelo}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{a.calibre}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block bg-compumaq-pretoSuave text-compumaq-texto text-xs font-medium px-2 py-0.5 rounded">
                        {a.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(a)} className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                        <button onClick={() => setDeleteTarget(a)} className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {meta && meta.totalPages > 1 && (
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={20} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Arma' : 'Nova Arma'}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField label="Atirador" required error={form.formState.errors.idatirador?.message}>
            <Controller control={form.control} name="idatirador" render={({ field }) => (
              <select className={selectClass} value={field.value || ''} onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}>
                <option value="">Selecione um atirador...</option>
                {atiradorData?.data?.map(a => (
                  <option key={a.idatirador} value={a.idatirador}>{a.nome} — {a.cpf}</option>
                ))}
              </select>
            )} />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Marca" required error={form.formState.errors.marca?.message}>
              <input className={inputClass} {...form.register('marca')} placeholder="Ex: Taurus" />
            </FormField>
            <FormField label="Modelo" required error={form.formState.errors.modelo?.message}>
              <input className={inputClass} {...form.register('modelo')} placeholder="Ex: PT100" />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Calibre" required error={form.formState.errors.calibre?.message}>
              <input className={inputClass} {...form.register('calibre')} placeholder="Ex: .40 S&W" />
            </FormField>
            <FormField label="Tipo" required error={form.formState.errors.tipo?.message}>
              <Controller control={form.control} name="tipo" render={({ field }) => (
                <select className={selectClass} value={field.value} onChange={field.onChange}>
                  <option value="">Selecione...</option>
                  {TIPOS_ARMA.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )} />
            </FormField>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {isSaving ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Arma"
        message={`Tem certeza que deseja excluir a arma "${deleteTarget?.marca} ${deleteTarget?.modelo}"?`}
        loading={deleteArma.isPending}
      />
    </div>
  )
}
